import { IApiaRadioButton, TComponentSubmitValues } from '../../types';
import ApiaField from '../ApiaField';
import ApiaForm from '../ApiaForm';
import Field from '../constants/Field';

import { IApiaField } from '../types';
import propsStore from '../../store/propsStore/propsStore';
import { arrayOrArray } from '../../utils/util';
import { TOption } from '../../api/apiaApi/fields/ApiaApiSelect';

export type TRadioOptionsAsObj = {
  [key: string]: string;
};

export interface IRadioField extends IApiaField {
  setValue(value: string | number): IRadioField;
  getOptions(asObject: boolean): TRadioOptionsAsObj | string[][] | null;
  clearOptions(): IRadioField | null;
  removeOption(radioValue: string | number): IRadioField | null;
  addOption(
    radioValue: string | number,
    showValue: string,
    allowRepeatedValue: boolean,
  ): IRadioField | null;
  getSelectedOption(
    asObject: boolean,
  ):
    | TRadioOptionsAsObj
    | string[][]
    | (string | number)[]
    | null
    | undefined
    | IRadioField;
  getSelectedText(): TComponentSubmitValues | null;
  getLabel(): string | null;
}

export default class RadioField extends ApiaField implements IRadioField {
  constructor(field: HTMLDivElement, form: ApiaForm) {
    super(field, form, [
      Field.PROPERTY_REQUIRED,
      Field.PROPERTY_DISABLED,
      Field.PROPERTY_READONLY,
      Field.PROPERTY_VISIBILITY_HIDDEN,
      Field.PROPERTY_SHOW_TOOLTIP_AS_HELP,
    ]);
  }

  setValue(value: string | number): IRadioField {
    // radio accepts strings only as values
    if (this.fldId) {
      super.setValue(value);
    }
    return this;
  }

  getOptions(asObject: boolean): TRadioOptionsAsObj | string[][] | null {
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const id = this.field.dataset.fieldId;
      let radioValues = (
        propsStore.getFieldProps(id as string)?.field as IApiaRadioButton
      ).possibleValue;

      if (!Array.isArray(radioValues)) {
        radioValues = [radioValues];
      }

      if (asObject) {
        const optionsResult = {} as TRadioOptionsAsObj;
        for (let i = 0; i < radioValues.length; i += 1) {
          optionsResult[radioValues[i].value || ''] =
            radioValues[i].label || '';
        }
        return optionsResult;
      }
      const optionsResult = [];
      for (let i = 0; i < radioValues.length; i += 1) {
        const currentOption = [];
        currentOption.push(radioValues[i].value || '');
        currentOption.push(radioValues[i].label || '');
        optionsResult.push(currentOption);
      }
      return optionsResult as string[][];
    }
    return null;
  }

  clearOptions(): IRadioField | null {
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const id = this.field.dataset.fieldId as string;
      propsStore.updateField(id, { possibleValue: [] });
      return this as IRadioField;
    }
    return null;
  }

  addOption(
    radioValue: string | number,
    showValue: string,
    allowRepeatedValue: boolean,
  ): IRadioField | null {
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const id = this.field.dataset.fieldId as string;
      const radioValues = arrayOrArray(
        propsStore.getFieldProps(id).possibleValue,
      );
      const copyRadioValues = [...radioValues];
      const isValueIsRepeated = radioValues.some((ele) => {
        return ele.value === radioValue.toString();
      });

      if (allowRepeatedValue || !isValueIsRepeated) {
        copyRadioValues.push({
          value: radioValue.toString(),
          label: showValue,
        });
        propsStore.updateField(id, { possibleValue: copyRadioValues });
      }
      return this as IRadioField;
    }
    return null;
  }

  setOptions(options: TOption[]): IRadioField | null {
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const id = this.field.dataset.fieldId as string;
      propsStore.updateField(id, { possibleValue: options });
      return this as IRadioField;
    }
    return null;
  }

  removeOption(radioValue: string | number): IRadioField | null {
    const optionsArray = [];
    if (this.checkFieldExistsInDocument(this.fldId) && this.fldId !== null) {
      const id = this.field.dataset.fieldId as string;
      const radioValues = (
        propsStore.getFieldProps(id)?.field as IApiaRadioButton
      ).possibleValue;
      for (let i = 0; i < radioValues.length; i += 1) {
        if (radioValue.toString() !== radioValues[i].value.toString()) {
          optionsArray.push({
            value: radioValues[i].value,
            label: radioValues[i].label,
          });
        }
      }
      const optionValues = optionsArray.map((option) => {
        return option.value;
      });
      propsStore.updateField(id, { possibleValue: optionsArray });
      // replace this.field to an existing radio node
      const radioNodes = document.querySelectorAll(`[data-field-id="${id}"]`);
      const currentNodes = Array.from(radioNodes).filter((radio) => {
        if (
          optionValues.indexOf(radio.getAttribute('value') as string) !== -1
        ) {
          return radio;
        }
        return null;
      });
      if (currentNodes.length > 0) {
        this.field = currentNodes[0] as HTMLInputElement;
      }
      return this as IRadioField;
    }
    return null;
  }

  getSelectedOption(
    asObject: boolean,
  ):
    | TRadioOptionsAsObj
    | string[][]
    | (string | number)[]
    | null
    | undefined
    | IRadioField {
    if (this.checkFieldExistsInDocument(this.fldId)) {
      const id = this.field.dataset.fieldId as string;
      const props = propsStore.getFieldProps(id);
      const radioValues = (
        propsStore.getFieldProps(id)?.field as IApiaRadioButton
      ).possibleValue;
      const { value } = props;

      if (value) {
        const selected = radioValues.find((ele) => {
          return ele.value === String(value);
        });
        if (asObject) {
          const optionsResult = {} as TRadioOptionsAsObj;
          optionsResult[selected?.value || ''] = selected?.label || '';
          return optionsResult;
        }
        const optionsResult = [];
        optionsResult.push(selected?.value || '');
        optionsResult.push(selected?.label || '');
        return optionsResult;
      }
      return this as IRadioField;
    }
    return null;
  }

  getSelectedText(): TComponentSubmitValues | null {
    if (this.checkFieldExistsInDocument(this.fldId)) {
      const id = this.field.dataset.fieldId as string;
      const props = propsStore.getFieldProps(id);
      const { possibleValue } = propsStore.getFieldProps(id)
        ?.field as IApiaRadioButton;
      const { value } = props;

      if (possibleValue && value) {
        const selected = possibleValue.find((possible) => {
          if (possible.value === value) {
            return possible;
          }
          return null;
        });
        return selected?.label;
      }
    }
    return null;
  }

  getLabel(): string | null {
    if (this.checkFieldExistsInDocument(this.fldId)) {
      if (this.fldId !== null) {
        const props = propsStore.getFieldProps(this.fldId);
        return props.attributes?.attLabel || '';
      }
    }
    return null;
  }
}
